import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Medal, Award } from "lucide-react";
import api from "../../lib/api";
import { Label, Avatar, scoreColor, EmptyState, InfoBadge } from "../../components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const METRICS = [
  { v: "ssi", l: "Service Index" },
  { v: "commendations", l: "Commendations" },
  { v: "training", l: "Training completions" },
  { v: "recovery", l: "Recovery resolved" },
];
const MEDAL = ["#F59E0B", "#94A3B8", "#B45309"];

export default function Leaderboards() {
  const navigate = useNavigate();
  const [scope, setScope] = useState("employee");
  const [metric, setMetric] = useState("ssi");
  const [period, setPeriod] = useState("90");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/leaderboards?scope=${scope}&metric=${metric}&period=${period}`).then((r) => setData(r.data)).catch(() => {});
  }, [scope, metric, period]);

  const fmt = (e) => metric === "ssi" ? e.value.toFixed(2) : e.value;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><span className="flex items-center gap-1.5"><Label>Recognition</Label>
          <InfoBadge title="Leaderboards" testId="lb-header-info"
            text="Ranks employees, locations or regions by a metric you choose (service score, commendations, training or recovery). Scores are confidence-adjusted, and anyone with too few verified ratings is flagged and shown last so rankings stay fair." /></span>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Leaderboards</h1></div>
        <div className="flex flex-wrap gap-2">
          <Select value={scope} onValueChange={setScope}><SelectTrigger className="w-36" data-testid="lb-scope"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="employee">Employees</SelectItem><SelectItem value="location">Locations</SelectItem><SelectItem value="region">Regions</SelectItem></SelectContent></Select>
          <Select value={metric} onValueChange={setMetric}><SelectTrigger className="w-48" data-testid="lb-metric"><SelectValue /></SelectTrigger>
            <SelectContent>{METRICS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent></Select>
          <Select value={period} onValueChange={setPeriod}><SelectTrigger className="w-28" data-testid="lb-period"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="7">7 days</SelectItem><SelectItem value="30">30 days</SelectItem><SelectItem value="90">90 days</SelectItem></SelectContent></Select>
        </div>
      </div>

      <p className="text-sm text-slate-500">Rankings use confidence-adjusted verified scores. Entries below the minimum sample are shown last and flagged.</p>

      {!data ? <div className="text-slate-400">Loading…</div> : data.entries.length === 0 ? (
        <EmptyState icon={Trophy} title="No ranked entries yet" hint="More verified interactions are needed." />
      ) : (
        <div className="space-y-2">
          {data.entries.map((e) => (
            <button key={e.id} data-testid={`lb-row-${e.id}`}
              onClick={() => scope === "employee" ? navigate(`/app/employees/${e.id}`) : null}
              className="flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left transition-transform hover:-translate-y-0.5">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full font-head font-bold"
                style={{ background: e.rank <= 3 ? MEDAL[e.rank - 1] : "#F1F5F9", color: e.rank <= 3 ? "#fff" : "#64748B" }}>
                {e.rank <= 3 ? <Medal className="h-5 w-5" /> : e.rank}
              </span>
              <Avatar name={e.name} src={e.photo_url} size={40} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-800">{e.name}</p>
                <p className="truncate text-xs text-slate-400">{e.subtitle || "—"} · {e.sample} verified</p>
              </div>
              {!e.meets_threshold && metric === "ssi" && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600">low sample</span>}
              <span className="sv-metric text-xl font-bold" style={{ color: metric === "ssi" ? scoreColor(e.value) : "#4338CA" }}>{fmt(e)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
